// src/components/invoices/CommentSection.jsx
import { useState, useEffect } from 'react';
import { Box, Card, TextField, Button, Typography, Divider, Avatar, Stack, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import invoiceService from 'services/invoiceService';
import { formatDateTime } from 'utils/formatters';

const CommentSection = ({ invoiceId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getComments(invoiceId);
      setComments(response.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      await invoiceService.addComment(invoiceId, newComment);
      setNewComment('');
      fetchComments();
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      submitted: 'info.main',
      approved: 'success.main',
      rejected: 'error.main',
      revised: 'warning.main',
      comment: 'text.primary'
    };
    return colors[action] || 'text.primary';
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Comments & Activity
      </Typography>

      {/* Comment List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mb: 3, maxHeight: 400, overflowY: 'auto' }}>
          {comments.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
              No comments yet
            </Typography>
          ) : (
            comments.map((comment) => (
              <Box key={comment.id}>
                <Stack direction="row" spacing={2}>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {comment.user ? `${comment.user.first_name?.[0] || ''}${comment.user.last_name?.[0] || ''}`.toUpperCase() : '-'}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="subtitle2">
                        {comment.user ? `${comment.user.first_name || ''} ${comment.user.last_name || ''}`.trim() : '-'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDateTime(comment.created_at)}
                      </Typography>
                      {comment.action !== 'comment' && (
                        <>
                          <Typography variant="caption" color="textSecondary">
                            •
                          </Typography>
                          <Typography variant="caption" sx={{ color: getActionColor(comment.action), fontWeight: 500 }}>
                            {comment.action.toUpperCase()}
                          </Typography>
                        </>
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {comment.comment}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))
          )}
        </Stack>
      )}

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={submitting}
        />
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            endIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={!newComment.trim() || submitting}
          >
            Add Comment
          </Button>
        </Box>
      </form>
    </Card>
  );
};

export default CommentSection;
